import React, { createContext, useState, useRef, useMemo, useContext, useEffect, useDebugValue, } from 'react';
import { isElement } from 'react-is';
import { useTimeout } from './useTimeout';
/**
 * Internal counter used to uniquely identify data dependencies.
 */
let uniqueId = 0;
const ActivityIndicatorContext = createContext({
    register() {
        return () => { };
    },
});
/**
 * Context for tracking the state of the activity indicator. We separate this
 * from the `ActivityIndicatorContext` as we expect this context to be consumed
 * infrequently, and don't want to force a rerender of all components sending
 * status to the indicator whenever that status changes.
 */
const ActivityStatusContext = createContext(false);
/**
 * The `ActivityIndicator` component provides a way to show some UI when there
 * are background data updates in progress. For example, this would be used to
 * render a thin indeterminate progress bar under a site's top nav.
 *
 * This component wraps around the components for which it should show an
 * indicator. It's common to have an instance near the root of the app, with
 * a handful of other uses for overlay or other similar components.
 */
export const ActivityIndicator = ({ indicator, children }) => {
    const registered = useRef(new Set());
    /**
     * Whether or not the indicator should be visible right now.
     */
    const [isIndicating, setIndicateStatus] = useState(false);
    /**
     * The value for the mutator function context. This is consumed by
     * `useActivityIndicator` to talk to this component. We memoize this
     * once so it never causes rerenders.
     */
    const registrar = useMemo(() => ({
        register() {
            const id = uniqueId++;
            const wasEmpty = !registered.current.size;
            registered.current.add(id);
            if (wasEmpty)
                setIndicateStatus(true);
            return () => {
                registered.current.delete(id);
                if (!registered.current.size)
                    setIndicateStatus(false);
            };
        },
    }), []);
    return (React.createElement(ActivityIndicatorContext.Provider, { value: registrar },
        React.createElement(ActivityStatusContext.Provider, { value: isIndicating },
            children,
            typeof indicator === 'function' && !isElement(indicator)
                ? indicator(isIndicating)
                : isIndicating && indicator)));
};
/**
 * Register a data dependency with the nearest `ActivityIndicator` parent to show
 * an indicator when there is stale data on display or a background operation in
 * progress.
 *
 * @param hasActivity Whether or not the hook caller has stale data (e.g. the
 * presented data isn't the latest data) or is performing some background task.
 * @param timeoutBeforeShow Amount of time in ms that `hasActivity` must be
 * `true` uninterrupted before it will ask the context to render the indicator.
 * This defaults to 250ms.
 */
export const useActivityIndicator = (hasActivity, timeoutBeforeShow = 250) => {
    const context = useContext(ActivityIndicatorContext);
    const [show, setShow] = useState(false);
    useTimeout(() => setShow(true), hasActivity && timeoutBeforeShow);
    useEffect(() => (show ? context.register() : undefined), [context, show]);
    useDebugValue(hasActivity ? (show ? 'Showing' : 'Waiting to show') : 'Not stale');
    if (!hasActivity && show)
        setShow(false);
};
/**
 * Get whether or not the ancestor `ActivityIndicator` is currently indicating.
 * This can be used to insert the visual indicator element as a descendant of
 * the context provider, rather than an immediate child.
 */
export const useActivityIndicatorStatus = () => {
    const status = useContext(ActivityStatusContext);
    useDebugValue(status);
    return status;
};
